import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Machine, ToolLibraryRecord, ToolSlotResponse } from '@bridgewerk/shared';
import { Button } from '../../components/ui/Button.js';
import { useAuth } from '../auth/useAuth.js';
import { getApiClient } from '../../lib/api-client.js';
import { machineKeys, toolKeys } from '../../lib/query-keys.js';
import { useToolLibrary } from '../tooling/hooks/useToolLibrary.js';
import { executeIntent, type ExecuteResult } from './executeIntent.js';
import type { VoiceIntentResponse, VoiceToolName } from './types.js';
import { useVoiceCapture } from './useVoiceCapture.js';

interface LogEntry {
  readonly transcript: string;
  readonly result: ExecuteResult;
}

type QueryResult =
  | { kind: 'machine_tools'; machineId: string; toolNumber?: number; slots: ToolSlotResponse[] }
  | { kind: 'tools_needing_attention'; status: string | null; items: Array<{ machineId: string; slot: ToolSlotResponse }> }
  | { kind: 'general_answer'; answer: string };

interface MissingInfo {
  readonly type: 'tool' | 'machine' | 'slot';
  readonly prompt: string;
  readonly machineId?: string;
  readonly toolLibraryId?: string;
}

type ModalState =
  | { kind: 'closed' }
  | { kind: 'capturing'; log: LogEntry[] }
  | { kind: 'sending'; log: LogEntry[] }
  | { kind: 'preview'; intent: VoiceIntentResponse; log: LogEntry[] }
  | { kind: 'executing'; intent: VoiceIntentResponse; log: LogEntry[] }
  | { kind: 'needs_info'; intent: VoiceIntentResponse; missing: MissingInfo; log: LogEntry[] }
  | { kind: 'query_result'; intent: VoiceIntentResponse; queryResult: QueryResult | null; log: LogEntry[] }
  | { kind: 'error'; message: string; log: LogEntry[] }
  | { kind: 'summary'; log: LogEntry[] };

interface IntentInfo {
  readonly icon: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly details: ReadonlyArray<readonly [string, string]>;
  readonly accentKey: 'green' | 'accent' | 'red' | 'dim';
}

function describeIntent(tool: VoiceToolName, args: Record<string, unknown>): IntentInfo {
  switch (tool) {
    case 'create_tool_library_entry': {
      const details: Array<readonly [string, string]> = [];
      if (args['manufacturer']) details.push(['Manufacturer', args['manufacturer'] as string]);
      if (args['part_number']) details.push(['Part No.', args['part_number'] as string]);
      if (args['type']) details.push(['Type', args['type'] as string]);
      if (args['material']) details.push(['Material', args['material'] as string]);
      if (args['diameter_mm'] != null) details.push(['Diameter', `${args['diameter_mm'] as number} mm`]);
      if (args['expected_life_cuts'] != null) details.push(['Life (cuts)', String(args['expected_life_cuts'])]);
      if (args['expected_life_minutes'] != null) details.push(['Life (min)', String(args['expected_life_minutes'])]);
      return { icon: '+', title: args['description'] as string, subtitle: 'Add to tool library', details, accentKey: 'green' };
    }
    case 'update_tool_library_entry': {
      const changes: Array<readonly [string, string]> = [];
      if (args['description']) changes.push(['Description', args['description'] as string]);
      if (args['manufacturer']) changes.push(['Manufacturer', args['manufacturer'] as string]);
      if (args['part_number']) changes.push(['Part No.', args['part_number'] as string]);
      if (args['type']) changes.push(['Type', args['type'] as string]);
      if (args['material']) changes.push(['Material', args['material'] as string]);
      if (args['diameter_mm'] != null) changes.push(['Diameter', `${args['diameter_mm'] as number} mm`]);
      return { icon: '✎', title: args['tool_description_hint'] as string, subtitle: 'Update tool library entry', details: changes, accentKey: 'accent' };
    }
    case 'delete_tool_library_entry':
      return { icon: '−', title: args['tool_description_hint'] as string, subtitle: 'Remove from tool library', details: [], accentKey: 'red' };
    case 'assign_tool_to_slot': {
      const slot = `T${String(args['tool_number'] as number).padStart(2, '0')}`;
      return {
        icon: '→',
        title: args['tool_description_hint'] as string,
        subtitle: `Assign to ${args['machine_id'] as string} · ${slot}`,
        details: [['Machine', args['machine_id'] as string], ['Slot', slot]],
        accentKey: 'accent',
      };
    }
    case 'remove_tool_from_slot': {
      const slot = `T${String(args['tool_number'] as number).padStart(2, '0')}`;
      return { icon: '−', title: `Clear ${slot}`, subtitle: `Remove from ${args['machine_id'] as string}`, details: [['Machine', args['machine_id'] as string], ['Slot', slot]], accentKey: 'red' };
    }
    case 'swap_tool_slots': {
      const a = `T${String(args['from_tool_number'] as number).padStart(2, '0')}`;
      const b = `T${String(args['to_tool_number'] as number).padStart(2, '0')}`;
      return { icon: '⇄', title: `${a} ↔ ${b}`, subtitle: `Swap on ${args['machine_id'] as string}`, details: [['Machine', args['machine_id'] as string], ['From', a], ['To', b]], accentKey: 'accent' };
    }
    case 'query_machine_tools': {
      const tn = args['tool_number'] as number | null | undefined;
      return {
        icon: '?',
        title: args['machine_id'] as string,
        subtitle: tn != null ? `Query slot T${String(tn).padStart(2, '0')}` : 'Query all slots',
        details: [],
        accentKey: 'dim',
      };
    }
    case 'query_tools_needing_attention':
      return { icon: '?', title: 'Tools needing attention', subtitle: (args['status'] as string | null | undefined) ?? 'all non-good', details: [], accentKey: 'dim' };
    case 'general_answer':
      return { icon: '?', title: 'Answer', subtitle: args['answer'] as string | undefined, details: [], accentKey: 'dim' };
    case 'unsupported_request':
      return { icon: '✕', title: 'Not supported', subtitle: args['reason'] as string | undefined, details: [], accentKey: 'dim' };
  }
}

function isQueryTool(tool: VoiceToolName): boolean {
  return tool === 'query_machine_tools' || tool === 'query_tools_needing_attention' || tool === 'general_answer';
}

// ── Missing-info inference ────────────────────────────────────────────────────

function inferMissingInfo(
  intent: VoiceIntentResponse,
  rejectionReason?: string,
  library?: readonly ToolLibraryRecord[],
): MissingInfo | null {
  // For assign_tool_to_slot: check each required field in priority order
  if (intent.tool === 'assign_tool_to_slot') {
    const args = intent.args as Record<string, unknown>;
    const hasId    = Boolean(args['tool_library_id']);
    const hasHint  = Boolean(args['tool_description_hint']);
    const hint     = (args['tool_description_hint'] as string | undefined ?? '').toLowerCase();
    const hasMatch = hasId || (hasHint && library?.some(t =>
      t.description.toLowerCase().includes(hint) || hint.includes(t.description.toLowerCase()),
    ));

    if (!hasMatch && !hasHint) return { type: 'tool',    prompt: 'Which tool to assign?' };
    if (!args['machine_id'])    return { type: 'machine', prompt: 'Which machine?' };
    if (!args['tool_number'] || (args['tool_number'] as number) === 0) {
      return {
        type:          'slot',
        prompt:        `Which pocket on ${args['machine_id'] as string} to load into?`,
        machineId:     args['machine_id'] as string,
        toolLibraryId: args['tool_library_id'] as string | undefined,
      };
    }
    return null;
  }

  // For unsupported/rejected: parse the reason text
  const reason = (
    rejectionReason ??
    (intent.tool === 'unsupported_request'
      ? ((intent.args as Record<string, unknown>)['reason'] as string | undefined) ?? ''
      : '')
  ).toLowerCase();

  if (!reason) return null;

  if (/slot|pocket|tool.number|which pocket|position number/i.test(reason)) {
    const args = intent.args as Record<string, unknown>;
    return {
      type:          'slot',
      prompt:        args['machine_id'] ? `Which pocket on ${args['machine_id'] as string}?` : 'Which pocket?',
      machineId:     args['machine_id'] as string | undefined,
      toolLibraryId: args['tool_library_id'] as string | undefined,
    };
  }
  if (/tool/.test(reason) && /which|specify|unclear|identify|don.t know|unknown|not found/.test(reason)) {
    return { type: 'tool', prompt: 'Which tool did you mean?' };
  }
  if (/machine/.test(reason) && /which|specify|unclear|identify|don.t know|unknown|not found/.test(reason)) {
    return { type: 'machine', prompt: 'Which machine?' };
  }
  return null;
}

// ── Gate ──────────────────────────────────────────────────────────────────────

export function VoiceAssistant(): JSX.Element | null {
  const enabled = (import.meta.env.VITE_VOICE_ENABLED ?? '').toLowerCase() === 'true';
  if (!enabled) return null;
  return <VoiceAssistantInner />;
}

// ── Main component ────────────────────────────────────────────────────────────

function VoiceAssistantInner(): JSX.Element {
  const { shopId } = useAuth();
  const qc = useQueryClient();
  const library = useToolLibrary(shopId);
  const machinesQuery = useQuery({
    queryKey: shopId ? machineKeys.list(shopId) : ['machines', 'none'],
    queryFn: () => getApiClient().getMachines(shopId ?? ''),
    enabled: !!shopId,
    staleTime: 60_000,
  });
  const capture = useVoiceCapture();
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [elapsed, setElapsed] = useState(0);
  const [fabPos, setFabPos] = useState<{ x: number; y: number } | null>(null);
  const [longPressing, setLongPressing] = useState(false);
  const fabDrag = useRef<{
    startX: number; startY: number; elX: number; elY: number; dragging: boolean;
    timer: ReturnType<typeof setTimeout> | null;
    trail: Array<{ x: number; y: number; t: number }>;
  } | null>(null);
  const momentumRaf = useRef<number | null>(null);

  const log = modal.kind !== 'closed' ? modal.log : [];

  const open  = () => { setModal({ kind: 'capturing', log: [] }); void capture.start(); };
  const close = () => { capture.cancel(); setModal({ kind: 'closed' }); };
  const done  = () => { capture.cancel(); setModal({ kind: 'summary', log }); };

  const clampFab = (x: number, y: number) => {
    const S = 56, NAV = 68, PAD = 8;
    return {
      x: Math.max(PAD, Math.min(window.innerWidth  - S - PAD, x)),
      y: Math.max(NAV, Math.min(window.innerHeight - S - PAD, y)),
    };
  };

  const applyMomentum = (vx: number, vy: number) => {
    if (momentumRaf.current !== null) cancelAnimationFrame(momentumRaf.current);
    const friction = 0.88;
    let curVx = vx, curVy = vy;
    const step = () => {
      curVx *= friction;
      curVy *= friction;
      if (Math.abs(curVx) < 0.15 && Math.abs(curVy) < 0.15) { momentumRaf.current = null; return; }
      setFabPos(prev => prev ? clampFab(prev.x + curVx, prev.y + curVy) : prev);
      momentumRaf.current = requestAnimationFrame(step);
    };
    momentumRaf.current = requestAnimationFrame(step);
  };

  const onFabPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (momentumRaf.current !== null) { cancelAnimationFrame(momentumRaf.current); momentumRaf.current = null; }
    const rect = e.currentTarget.getBoundingClientRect();
    const timer = setTimeout(() => {
      if (fabDrag.current) {
        fabDrag.current.dragging = true;
        setLongPressing(true);
        if (navigator.vibrate) navigator.vibrate(25);
      }
    }, 250);
    fabDrag.current = {
      startX: e.clientX, startY: e.clientY,
      elX: rect.left, elY: rect.top,
      dragging: false,
      timer,
      trail: [{ x: e.clientX, y: e.clientY, t: Date.now() }],
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onFabPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!fabDrag.current?.dragging) return;
    const dx = e.clientX - fabDrag.current.startX;
    const dy = e.clientY - fabDrag.current.startY;
    const pos = clampFab(fabDrag.current.elX + dx, fabDrag.current.elY + dy);
    const now = Date.now();
    fabDrag.current.trail.push({ x: e.clientX, y: e.clientY, t: now });
    if (fabDrag.current.trail.length > 6) fabDrag.current.trail.shift();
    setFabPos(pos);
  };

  const onFabPointerUp = () => {
    if (!fabDrag.current) return;
    if (fabDrag.current.timer) clearTimeout(fabDrag.current.timer);
    const { dragging, trail } = fabDrag.current;
    fabDrag.current = null;
    setLongPressing(false);
    if (!dragging) { open(); return; }
    // Calculate release velocity from recent trail (px per 16ms frame)
    if (trail.length >= 2) {
      const last = trail[trail.length - 1]!;
      const ref  = trail[Math.max(0, trail.length - 3)]!;
      const dt   = Math.max(1, last.t - ref.t);
      applyMomentum((last.x - ref.x) / dt * 16, (last.y - ref.y) / dt * 16);
    }
  };

  useEffect(() => {
    if (modal.kind === 'closed') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal.kind]);

  // Live recording timer
  useEffect(() => {
    if (capture.state !== 'recording') { setElapsed(0); return; }
    setElapsed(0);
    const id = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [capture.state]);

  const fetchQueryResult = async (intent: VoiceIntentResponse): Promise<void> => {
    try {
      let result: QueryResult;
      const args = intent.args as Record<string, unknown>;

      if (intent.tool === 'general_answer') {
        result = { kind: 'general_answer', answer: (args['answer'] as string | undefined) ?? '' };
      } else if (intent.tool === 'query_machine_tools') {
        if (!shopId) throw new Error('No shop selected.');
        const machineId = args['machine_id'] as string;
        const toolNumber = args['tool_number'] as number | null | undefined;
        const data = await getApiClient().getMachineTools(shopId, machineId);
        const slots = toolNumber != null
          ? data.tools.filter(t => t.tool_number === toolNumber)
          : data.tools.slice().sort((a, b) => a.tool_number - b.tool_number);
        result = { kind: 'machine_tools', machineId, toolNumber: toolNumber ?? undefined, slots };
      } else if (intent.tool === 'query_tools_needing_attention') {
        if (!shopId) throw new Error('No shop selected.');
        const statusFilter = (args['status'] as string | null | undefined) ?? null;
        const machines = machinesQuery.data ?? [];
        const perMachine = await Promise.all(
          machines.map(m =>
            getApiClient().getMachineTools(shopId, m.machine_id)
              .then(d => d.tools.map(slot => ({ machineId: m.machine_id, slot })))
              .catch(() => [] as Array<{ machineId: string; slot: ToolSlotResponse }>),
          ),
        );
        let items = perMachine.flat().filter(({ slot }) => slot.tool_definition !== null);
        items = statusFilter
          ? items.filter(({ slot }) => slot.status === statusFilter)
          : items.filter(({ slot }) => slot.status !== 'good');
        result = { kind: 'tools_needing_attention', status: statusFilter, items };
      } else {
        return;
      }

      setModal(s => s.kind === 'query_result' ? { ...s, queryResult: result } : s);
    } catch (err) {
      setModal(s => s.kind !== 'closed'
        ? { kind: 'error', message: err instanceof Error ? err.message : 'Query failed.', log: s.log }
        : s);
    }
  };

  const handleStop = async () => {
    const blob = await capture.stop();
    if (!blob) {
      setModal(s => s.kind !== 'closed'
        ? { kind: 'error', message: capture.error ?? 'No audio captured.', log: s.log }
        : s);
      return;
    }
    setModal(s => s.kind !== 'closed' ? { kind: 'sending', log: s.log } : s);
    try {
      const context = buildVoiceContext(machinesQuery.data, library.data);
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Check your connection and try again.')), 10_000),
      );
      const intent = (await Promise.race([
        getApiClient().resolveVoiceIntent(blob, context),
        timeout,
      ])) as VoiceIntentResponse;
      if (isQueryTool(intent.tool)) {
        setModal(s => s.kind !== 'closed' ? { kind: 'query_result', intent, queryResult: null, log: s.log } : s);
        void fetchQueryResult(intent);
      } else {
        setModal(s => s.kind !== 'closed' ? { kind: 'preview', intent, log: s.log } : s);
      }
    } catch (err) {
      setModal(s => s.kind !== 'closed'
        ? { kind: 'error', message: err instanceof Error ? err.message : 'Voice request failed.', log: s.log }
        : s);
    }
  };

  const executeAndHandle = async () => {
    if (modal.kind !== 'preview' || !shopId) return;
    const { intent } = modal;
    const currentLog = modal.log;
    setModal({ kind: 'executing', intent, log: currentLog });
    try {
      const result = await executeIntent(intent, { shopId, library: library.data });
      if (result.kind === 'created_library_entry' || result.kind === 'updated_library_entry' || result.kind === 'deleted_library_entry') {
        void qc.invalidateQueries({ queryKey: toolKeys.library(shopId) });
      } else if (result.kind === 'assigned_slot' || result.kind === 'removed_slot' || result.kind === 'swapped_slots') {
        void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, result.machineId) });
      }
      if (result.kind === 'rejected') {
        const missing = inferMissingInfo(intent, result.reason, library.data);
        if (missing) {
          setModal({ kind: 'needs_info', intent, missing, log: currentLog });
          return;
        }
      }
      const entry: LogEntry = { transcript: intent.transcript, result };
      setModal({ kind: 'capturing', log: [...currentLog, entry] });
      void capture.start();
    } catch (err) {
      setModal({ kind: 'error', message: err instanceof Error ? err.message : 'Execution failed.', log: currentLog });
    }
  };

  const handleInfoSelected = async (item: ToolLibraryRecord | Machine) => {
    if (modal.kind !== 'needs_info' || !shopId) return;
    const { intent, log: currentLog } = modal;

    // Merge the picked item directly — no GPT round-trip needed
    const isTool = 'tool_id' in item;
    const mergedArgs: Record<string, unknown> = isTool
      ? { ...intent.args as Record<string, unknown>, tool_library_id: item.tool_id, tool_description_hint: item.description }
      : { ...intent.args as Record<string, unknown>, machine_id: item.machine_id };

    // Pickers only appear for assign flows; escalate unsupported_request → assign_tool_to_slot
    const effectiveTool: VoiceToolName = intent.tool === 'unsupported_request' ? 'assign_tool_to_slot' : intent.tool;
    const baseArgs = effectiveTool === 'assign_tool_to_slot' && !('tool_number' in mergedArgs)
      ? { tool_number: 0, ...mergedArgs }
      : mergedArgs;

    const updatedIntent: VoiceIntentResponse = { ...intent, tool: effectiveTool, args: baseArgs };

    // Chase remaining missing info
    const stillMissing = inferMissingInfo(updatedIntent, undefined, library.data);
    if (stillMissing) {
      setModal({ kind: 'needs_info', intent: updatedIntent, missing: stillMissing, log: currentLog });
      return;
    }

    // All info collected — execute immediately
    setModal({ kind: 'executing', intent: updatedIntent, log: currentLog });
    try {
      const result = await executeIntent(updatedIntent, { shopId, library: library.data });

      if (result.kind === 'created_library_entry' || result.kind === 'updated_library_entry' || result.kind === 'deleted_library_entry') {
        void qc.invalidateQueries({ queryKey: toolKeys.library(shopId) });
      } else if (result.kind === 'assigned_slot' || result.kind === 'removed_slot' || result.kind === 'swapped_slots') {
        void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, result.machineId) });
      }

      if (result.kind === 'rejected') {
        setModal({ kind: 'error', message: result.reason ?? 'Execution failed.', log: currentLog });
        return;
      }

      const entry: LogEntry = { transcript: intent.transcript, result };
      setModal({ kind: 'capturing', log: [...currentLog, entry] });
      void capture.start();
    } catch (err) {
      setModal({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Request failed.',
        log: currentLog,
      });
    }
  };

  const handleSlotSelected = async (toolNumber: number) => {
    if (modal.kind !== 'needs_info' || !shopId) return;
    const { intent, missing, log: currentLog } = modal;
    if (missing.type !== 'slot' || !missing.machineId || !missing.toolLibraryId) {
      setModal({ kind: 'error', message: 'Lost track of which tool to assign — please try again.', log: currentLog });
      return;
    }
    setModal({ kind: 'executing', intent, log: currentLog });
    try {
      await getApiClient().assignToolToSlot(shopId, missing.machineId, toolNumber, missing.toolLibraryId);
      void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, missing.machineId) });
      void qc.invalidateQueries({ queryKey: toolKeys.graveyard(shopId) });
      const result: ExecuteResult = { kind: 'assigned_slot', machineId: missing.machineId, toolNumber, toolLibraryId: missing.toolLibraryId };
      const entry: LogEntry = { transcript: intent.transcript, result };
      setModal({ kind: 'capturing', log: [...currentLog, entry] });
      void capture.start();
    } catch (err) {
      setModal({ kind: 'error', message: err instanceof Error ? err.message : 'Assignment failed.', log: currentLog });
    }
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return createPortal(
    <>
      {/* Draggable FAB — tap to open, drag to reposition */}
      <button
        type="button"
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onPointerCancel={() => {
          if (fabDrag.current?.timer) clearTimeout(fabDrag.current.timer);
          fabDrag.current = null;
          setLongPressing(false);
        }}
        aria-label="Open voice assistant"
        className={`fixed z-40 h-14 w-14 rounded-full border border-accent bg-card text-text flex items-center justify-center touch-none select-none transition-transform duration-150 ${longPressing ? 'scale-110 cursor-grabbing border-2' : 'cursor-grab'}`}
        style={fabPos
          ? { left: fabPos.x, top: fabPos.y, boxShadow: '0 0 0 1px var(--color-accent), 0 4px 24px rgba(0,0,0,0.45)' }
          : { bottom: '1.5rem', right: '1rem', boxShadow: '0 0 0 1px var(--color-accent), 0 4px 24px rgba(0,0,0,0.45)' }
        }
      >
        <MicIcon large />
      </button>

      {modal.kind !== 'closed' && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="voice-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-surface shadow-2xl overflow-hidden">

            <header className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 id="voice-modal-title" className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-dim">
                  Voice assistant
                </h2>
                {log.length > 0 && (
                  <span className="font-mono text-[10px] bg-accent/15 text-accent px-1.5 py-0.5 rounded">
                    {log.length} done
                  </span>
                )}
              </div>
              <button type="button" onClick={close} aria-label="Close" className="text-text-dim hover:text-text ml-4 leading-none">✕</button>
            </header>

            <div className="p-5">
              {modal.kind === 'capturing' && (
                <RecordView
                  captureState={capture.state}
                  error={capture.error}
                  analyser={capture.analyser}
                  elapsed={elapsed}
                  fmt={fmt}
                  log={modal.log}
                  onStart={() => { void capture.start(); }}
                  onStop={() => { void handleStop(); }}
                />
              )}
              {modal.kind === 'sending'      && <SpinnerView label="Transcribing your request…" />}
              {modal.kind === 'preview'      && <PreviewView intent={modal.intent} />}
              {modal.kind === 'executing'    && <SpinnerView label="Executing…" />}
              {modal.kind === 'query_result' && <QueryResultView intent={modal.intent} queryResult={modal.queryResult} />}
              {modal.kind === 'needs_info' && (
                <NeedsInfoView
                  missing={modal.missing}
                  tools={library.data}
                  machines={machinesQuery.data}
                  shopId={shopId ?? ''}
                  onSelect={(item) => { void handleInfoSelected(item); }}
                  onSelectSlot={(n) => { void handleSlotSelected(n); }}
                />
              )}
              {modal.kind === 'error' && (
                <div className="text-center py-2">
                  <p className="text-2xl mb-3">⚠</p>
                  <p className="text-sm text-red font-mono">{modal.message}</p>
                </div>
              )}
              {modal.kind === 'summary' && <SummaryView log={modal.log} />}
            </div>

            <footer className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              {modal.kind === 'summary' ? (
                <Button type="button" variant="primary" onClick={close}>Close</Button>
              ) : modal.kind === 'preview' ? (
                (() => {
                  const missing = inferMissingInfo(modal.intent, undefined, library.data);
                  return (
                    <>
                      <Button type="button" variant="ghost" onClick={() => setModal({ kind: 'capturing', log: modal.log })}>Back</Button>
                      {modal.intent.tool === 'unsupported_request' && missing ? (
                        <Button type="button" variant="primary" onClick={() => setModal({ kind: 'needs_info', intent: modal.intent, missing, log: modal.log })}>
                          Select {missing.type} →
                        </Button>
                      ) : modal.intent.tool !== 'unsupported_request' ? (
                        <Button type="button" variant="primary" onClick={() => { void executeAndHandle(); }}>Confirm &amp; execute</Button>
                      ) : null}
                    </>
                  );
                })()
              ) : modal.kind === 'needs_info' ? (
                <Button type="button" variant="ghost" onClick={() => setModal({ kind: 'capturing', log: modal.log })}>
                  Cancel
                </Button>
              ) : modal.kind === 'query_result' ? (
                <>
                  <Button type="button" variant="ghost" onClick={log.length > 0 ? done : close}>
                    {log.length > 0 ? 'Done' : 'Close'}
                  </Button>
                  <Button type="button" variant="primary" onClick={() => { setModal({ kind: 'capturing', log: modal.log }); void capture.start(); }}>
                    Record again
                  </Button>
                </>
              ) : modal.kind === 'error' ? (
                <>
                  <Button type="button" variant="ghost" onClick={log.length > 0 ? done : close}>
                    {log.length > 0 ? 'Done' : 'Cancel'}
                  </Button>
                  <Button type="button" variant="primary" onClick={() => setModal({ kind: 'capturing', log })}>Try again</Button>
                </>
              ) : (
                <Button type="button" variant="ghost" onClick={log.length > 0 ? done : close}>
                  {log.length > 0 ? 'Done' : 'Cancel'}
                </Button>
              )}
            </footer>

          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

// ── Recording view ────────────────────────────────────────────────────────────

function RecordView({
  captureState, error, analyser, elapsed, fmt, log, onStart, onStop,
}: {
  readonly captureState: ReturnType<typeof useVoiceCapture>['state'];
  readonly error: string | null;
  readonly analyser: AnalyserNode | null;
  readonly elapsed: number;
  readonly fmt: (s: number) => string;
  readonly log: LogEntry[];
  readonly onStart: () => void;
  readonly onStop: () => void;
}): JSX.Element {
  if (captureState === 'unsupported') {
    return <p className="text-center text-sm text-text-dim font-mono">Voice capture is not supported in this browser.</p>;
  }
  if (captureState === 'denied') {
    return <p className="text-center text-sm text-red font-mono">{error ?? 'Microphone permission denied.'}</p>;
  }

  const isRecording = captureState === 'recording';

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Waveform / idle bar display */}
      <div className="w-full h-16 rounded-lg bg-card border border-border overflow-hidden">
        {isRecording && analyser != null ? <WaveformBars analyser={analyser} /> : <IdleWave />}
      </div>

      {/* Live timer */}
      <div className={`font-mono text-3xl font-bold tabular-nums transition-colors ${isRecording ? 'text-red' : 'text-text-dim'}`}>
        {fmt(elapsed)}
      </div>

      {/* Push-to-talk button */}
      <button
        type="button"
        onClick={isRecording ? onStop : onStart}
        className={[
          'h-20 w-20 rounded-full border-2 transition-all duration-150 flex items-center justify-center select-none',
          isRecording
            ? 'border-red bg-red/20 animate-pulse scale-110'
            : 'border-accent bg-accent/10 hover:bg-accent/20 active:scale-95',
        ].join(' ')}
        aria-label={isRecording ? 'Tap to send' : 'Tap to talk'}
      >
        <MicIcon large />
      </button>

      <p className="text-xs font-mono text-text-dim">
        {isRecording ? 'Listening… tap to send' : 'Tap to start'}
      </p>

      {error && <p className="text-xs text-red font-mono text-center">{error}</p>}

      {log.length > 0 && (
        <div className="w-full space-y-1 mt-1">
          {log.slice(-2).map((entry, i) => (
            <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-card border border-border">
              <MiniResultLine result={entry.result} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Waveform canvas ───────────────────────────────────────────────────────────

function WaveformBars({ analyser }: { readonly analyser: AnalyserNode }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufLen = analyser.frequencyBinCount;
    const data = new Uint8Array(bufLen);
    let rafId = 0;

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(data);
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      const count = Math.min(40, bufLen);
      const barW = width / count - 1.5;
      for (let i = 0; i < count; i++) {
        const v = (data[i] ?? 0) / 255;
        const h = Math.max(2, v * height);
        // Accent color #00C4E8 with brightness-scaled alpha
        ctx.fillStyle = `rgba(0,196,232,${0.25 + v * 0.75})`;
        ctx.fillRect(i * (barW + 1.5), height - h, barW, h);
      }
    };
    draw();
    return () => cancelAnimationFrame(rafId);
  }, [analyser]);

  return <canvas ref={canvasRef} className="w-full h-full block" />;
}

function IdleWave(): JSX.Element {
  return (
    <div className="w-full h-full flex items-center justify-center gap-[3px] px-4">
      {Array.from({ length: 28 }, (_, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-border"
          style={{ height: `${5 + Math.abs(Math.sin(i * 0.65)) * 12}px` }}
        />
      ))}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────

function SpinnerView({ label }: { readonly label: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      <div className="h-10 w-10 rounded-full border-2 border-accent border-t-transparent animate-spin" />
      <p className="text-sm font-mono text-text-dim">{label}</p>
    </div>
  );
}

// ── Preview ───────────────────────────────────────────────────────────────────

function PreviewView({ intent }: { readonly intent: VoiceIntentResponse }): JSX.Element {
  const info = describeIntent(intent.tool, intent.args as Record<string, unknown>);

  const borderCls = { green: 'border-green', accent: 'border-accent', red: 'border-red', dim: 'border-border' }[info.accentKey];
  const iconCls   = { green: 'text-green',   accent: 'text-accent',   red: 'text-red',   dim: 'text-text-dim' }[info.accentKey];

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-1">Heard</p>
        <p className="text-sm italic text-text-dim">&ldquo;{intent.transcript}&rdquo;</p>
      </div>

      <div className={`rounded-lg border-2 ${borderCls} bg-card p-4`}>
        <div className="flex items-start gap-3 mb-3">
          <span className={`font-mono text-lg leading-none mt-0.5 ${iconCls}`}>{info.icon}</span>
          <div>
            <p className="text-sm font-semibold text-text leading-snug">{info.title}</p>
            {info.subtitle && (
              <p className="text-[11px] text-text-dim font-mono mt-0.5">{info.subtitle}</p>
            )}
          </div>
        </div>

        {info.details.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs pt-3 border-t border-border">
            {info.details.map(([label, value]) => (
              <div key={label}>
                <dt className="font-mono text-[9px] uppercase tracking-widest text-text-dim">{label}</dt>
                <dd className="font-mono text-text mt-0.5">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

// ── Summary ───────────────────────────────────────────────────────────────────

function SummaryView({ log }: { readonly log: LogEntry[] }): JSX.Element {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (i: number) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  if (log.length === 0) {
    return <p className="text-center text-sm text-text-dim font-mono py-4">No changes made this session.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-3">Session summary</p>
      {log.map((entry, i) => {
        const open = expanded.has(i);
        return (
          <div key={i} className="rounded-md border border-border bg-card overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(i)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
            >
              <span className="flex-1 min-w-0">
                <MiniResultLine result={entry.result} />
              </span>
              <span className="shrink-0 text-[10px] text-text-dim leading-none">{open ? '▲' : '▼'}</span>
            </button>

            {open && (
              <div className="border-t border-border px-3 py-2.5 space-y-2">
                <div>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted mb-0.5">You said</p>
                  <p className="text-xs font-mono text-text-dim italic">&ldquo;{entry.transcript}&rdquo;</p>
                </div>
                {entry.result.kind === 'rejected' && (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-widest text-muted mb-0.5">Reason</p>
                    <p className="text-xs font-mono text-red">{entry.result.reason}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MiniResultLine({ result }: { readonly result: ExecuteResult }): JSX.Element {
  switch (result.kind) {
    case 'created_library_entry':
      return <p className="text-xs font-mono text-green truncate">+ Added · {result.record.description}</p>;
    case 'updated_library_entry':
      return <p className="text-xs font-mono text-accent truncate">✎ Updated · {result.record.description}</p>;
    case 'deleted_library_entry':
      return <p className="text-xs font-mono text-red truncate">− Deleted · {result.description}</p>;
    case 'assigned_slot':
      return <p className="text-xs font-mono text-green truncate">→ Assigned · {result.machineId} T{String(result.toolNumber).padStart(2, '0')}</p>;
    case 'removed_slot':
      return <p className="text-xs font-mono text-red truncate">− Cleared T{String(result.toolNumber).padStart(2, '0')} · {result.machineId}</p>;
    case 'swapped_slots':
      return <p className="text-xs font-mono text-accent truncate">⇄ Swapped T{String(result.fromNum).padStart(2, '0')} ↔ T{String(result.toNum).padStart(2, '0')} · {result.machineId}</p>;
    case 'rejected':
      return <p className="text-xs font-mono text-text-dim truncate">✕ Rejected · {result.reason}</p>;
  }
}

// ── Needs-info picker ─────────────────────────────────────────────────────────

function NeedsInfoView({
  missing,
  tools,
  machines,
  shopId,
  onSelect,
  onSelectSlot,
}: {
  readonly missing: MissingInfo;
  readonly tools: ToolLibraryRecord[] | undefined;
  readonly machines: Machine[] | undefined;
  readonly shopId: string;
  readonly onSelect: (item: ToolLibraryRecord | Machine) => void;
  readonly onSelectSlot: (toolNumber: number) => void;
}): JSX.Element {
  if (missing.type === 'slot') {
    return (
      <SlotPickerView
        machineId={missing.machineId ?? ''}
        shopId={shopId}
        prompt={missing.prompt}
        onSelectSlot={onSelectSlot}
      />
    );
  }

  const items = missing.type === 'tool' ? (tools ?? []) : (machines ?? []);

  return (
    <div className="space-y-3">
      <p className="text-sm font-mono text-text-dim text-center">{missing.prompt}</p>
      {items.length === 0 ? (
        <p className="text-xs font-mono text-muted text-center py-4">
          No {missing.type === 'tool' ? 'tools in the library' : 'machines configured'}.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {missing.type === 'tool'
            ? (items as ToolLibraryRecord[]).map(t => (
                <button
                  key={t.tool_id}
                  type="button"
                  onClick={() => onSelect(t)}
                  className="w-full text-left px-3 py-2.5 rounded-md border border-border bg-card hover:border-accent hover:bg-accent/5 transition-colors"
                >
                  <p className="text-sm font-mono text-text truncate">{t.description}</p>
                  <p className="text-[10px] font-mono text-text-dim">
                    {t.type} · {t.material}{t.diameter_mm != null ? ` · ${t.diameter_mm} mm` : ''}
                  </p>
                </button>
              ))
            : (items as Machine[]).map(m => (
                <button
                  key={m.machine_id}
                  type="button"
                  onClick={() => onSelect(m)}
                  className="w-full text-left px-3 py-2.5 rounded-md border border-border bg-card hover:border-accent hover:bg-accent/5 transition-colors"
                >
                  <p className="text-sm font-mono text-text">{m.machine_id}</p>
                  <p className="text-[10px] font-mono text-text-dim">{m.controller_type}</p>
                </button>
              ))
          }
        </div>
      )}
    </div>
  );
}

// ── Slot picker ───────────────────────────────────────────────────────────────

function SlotPickerView({
  machineId,
  shopId,
  prompt,
  onSelectSlot,
}: {
  readonly machineId: string;
  readonly shopId: string;
  readonly prompt: string;
  readonly onSelectSlot: (toolNumber: number) => void;
}): JSX.Element {
  const [slots, setSlots] = React.useState<ToolSlotResponse[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setFetchError(null);
    getApiClient().getMachineTools(shopId, machineId)
      .then(r => { setSlots(r.tools); setLoading(false); })
      .catch(err => { setFetchError(err instanceof Error ? err.message : 'Failed to load slots'); setLoading(false); });
  }, [shopId, machineId]);

  if (loading)    return <div className="py-4 text-center text-xs font-mono text-text-dim">Loading pockets…</div>;
  if (fetchError) return <div className="py-4 text-center text-xs font-mono text-red">{fetchError}</div>;

  const occupied = (slots ?? []).filter(s => s.tool_definition !== null);

  return (
    <div className="space-y-3">
      <p className="text-sm font-mono text-text-dim text-center">{prompt}</p>
      <p className="text-[10px] font-mono text-muted text-center">The current tool in that pocket will be retired.</p>
      {occupied.length === 0 ? (
        <p className="text-xs font-mono text-text-dim text-center py-2">No occupied pockets on {machineId}.</p>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {occupied.map(slot => (
            <button
              key={slot.tool_number}
              type="button"
              onClick={() => onSelectSlot(slot.tool_number)}
              className="w-full text-left px-3 py-2.5 rounded-md border border-border bg-card hover:border-red/40 hover:bg-red/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-[11px] font-mono font-semibold text-text w-8">
                  T{String(slot.tool_number).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-text truncate">{slot.tool_definition!.description}</p>
                  <p className="text-[10px] font-mono text-text-dim">{slot.life_percent}% life · {slot.status}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Query result view ─────────────────────────────────────────────────────────

const QUERY_STATUS_COLORS: Record<string, string> = {
  change: 'text-red border-red/30 bg-red/10',
  order:  'text-amber border-amber/30 bg-amber/10',
  watch:  'text-yellow border-yellow/30 bg-yellow/10',
  good:   'text-green border-green/30 bg-green/10',
};

function QueryResultView({
  intent,
  queryResult,
}: {
  readonly intent: VoiceIntentResponse;
  readonly queryResult: QueryResult | null;
}): JSX.Element {
  if (!queryResult) return <SpinnerView label="Looking up…" />;

  const heard = (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-1">Heard</p>
      <p className="text-sm italic text-text-dim">&ldquo;{intent.transcript}&rdquo;</p>
    </div>
  );

  if (queryResult.kind === 'general_answer') {
    return (
      <div className="space-y-3">
        {heard}
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-sm font-mono text-text leading-relaxed">{queryResult.answer}</p>
        </div>
      </div>
    );
  }

  if (queryResult.kind === 'machine_tools') {
    const heading = queryResult.toolNumber != null
      ? `${queryResult.machineId} · T${String(queryResult.toolNumber).padStart(2, '0')}`
      : queryResult.machineId;
    return (
      <div className="space-y-3">
        {heard}
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">{heading}</p>
        {queryResult.slots.length === 0 ? (
          <p className="text-sm font-mono text-text-dim text-center py-2">No tools found.</p>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {queryResult.slots.map(slot => (
              <QuerySlotRow key={slot.tool_number} slot={slot} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (queryResult.kind === 'tools_needing_attention') {
    const label = queryResult.status ?? 'all non-good';
    return (
      <div className="space-y-3">
        {heard}
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">
          Needs attention · {label}
        </p>
        {queryResult.items.length === 0 ? (
          <p className="text-sm font-mono text-green text-center py-2">All tools in good shape.</p>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {queryResult.items.map(({ machineId, slot }) => (
              <QuerySlotRow key={`${machineId}-${slot.tool_number}`} slot={slot} machineId={machineId} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return <></>;
}

function QuerySlotRow({
  slot,
  machineId,
}: {
  readonly slot: ToolSlotResponse;
  readonly machineId?: string;
}): JSX.Element {
  const statusCls = QUERY_STATUS_COLORS[slot.status] ?? 'text-text-dim border-border bg-transparent';
  const def = slot.tool_definition;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
      <span className="shrink-0 text-[11px] font-mono font-semibold text-text w-8">
        T{String(slot.tool_number).padStart(2, '0')}
      </span>
      <div className="flex-1 min-w-0">
        {machineId && <p className="text-[9px] font-mono text-muted">{machineId}</p>}
        <p className="text-xs font-mono text-text truncate">
          {def ? def.description : <span className="italic text-muted">No identity</span>}
        </p>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border ${statusCls}`}>
          {slot.status}
        </span>
        <span className="text-[9px] font-mono text-muted">{slot.life_percent}%</span>
      </div>
    </div>
  );
}

// ── Voice context ─────────────────────────────────────────────────────────────

function buildVoiceContext(
  machines: Machine[] | undefined,
  library: ToolLibraryRecord[] | undefined,
  hint?: string, // extra context injected by the info-picker flow
): string {
  const lines: string[] = [];
  lines.push(
    'CNC brands: FANUC (sounds like FAN-uck), HAAS (sounds like HOSS), ' +
    'MAZAK, OKUMA, DMG MORI, DOOSAN, MAKINO, HURCO, BROTHER, CITIZEN, ' +
    'NAKAMURA, KITAMURA, MORI SEIKI.',
  );
  if (machines && machines.length > 0) {
    lines.push(`Machines: ${machines.map(m => `${m.machine_id} (${m.controller_type})`).join(', ')}.`);
  }
  if (library && library.length > 0) {
    const parts = library.map(t => t.part_number ? `${t.description} (PN ${t.part_number})` : t.description);
    let toolLine = `Tool library: ${parts.join(', ')}.`;
    if (toolLine.length > 400) {
      toolLine = `Tool library: ${parts.slice(0, 15).join(', ')}${library.length > 15 ? ` and ${library.length - 15} more` : ''}.`;
    }
    lines.push(toolLine);
  }
  if (hint) lines.push(hint);
  return lines.join('\n');
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function MicIcon({ large = false }: { readonly large?: boolean }): JSX.Element {
  const size = large ? 28 : 16;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" strokeLinecap="round" />
    </svg>
  );
}
